import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../config/firebase';
import { logAuthEvent } from '../utils/logging';
import './Signup.css';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Log signup event (non-blocking — don't let logging failures break the flow)
      logAuthEvent({
        event: 'signup',
        email: email.toLowerCase(),
        userId: userCredential.user.uid,
        details: {
          emailVerified: userCredential.user.emailVerified,
          timestamp: new Date()
        }
      }).catch(logErr => console.error('Auth log failed:', logErr));
      
      // Send verification email — this is the critical step
      try {
        await sendEmailVerification(userCredential.user);
      } catch (emailErr: any) {
        console.error('Failed to send verification email:', emailErr);
        logAuthEvent({
          event: 'signup',
          email: email.toLowerCase(),
          error: `Verification email failed: ${emailErr.message}`,
          details: { errorCode: emailErr.code }
        }).catch(() => {});
        
        // Still navigate to email-sent but warn the user
        navigate('/email-sent', { 
          state: { 
            email: email.toLowerCase(),
            isNewAccount: true,
            fromSignup: true,
            preventAuthRedirect: true,
            emailSendFailed: true
          },
          replace: true
        });
        return;
      }
      
      navigate('/email-sent', { 
        state: { 
          email: email.toLowerCase(),
          isNewAccount: true,
          fromSignup: true,
          preventAuthRedirect: true
        },
        replace: true
      });
      
      return;
    } catch (err: any) {
      console.error('Signup error:', err);
      
      logAuthEvent({
        event: 'signup',
        email: email.toLowerCase(),
        error: err.message,
        details: {
          errorCode: err.code,
          errorStack: err.stack
        }
      }).catch(() => {});

      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else {
        setError(err.message || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <h2>Create Account</h2>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div>
          <label>Email:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            placeholder="Enter your email"
          />
        </div>
        <div className="password-field">
          <label>Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        <div className="password-field">
          <label>Confirm Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
      <div className="auth-links">
        <p className="auth-switch">
          Already have an account?{' '}
          <button 
            className="link-button"
            onClick={() => navigate('/login')}
            disabled={loading}
          >
            Log In
          </button>
        </p>
        <button 
          className="link-button"
          onClick={() => navigate('/reset-password')}
          disabled={loading}
        >
          Forgot Password?
        </button>
      </div>
    </div>
  );
};

export default Signup; 